// "Labeled card" + datagrid building blocks — the shared layout for
// detail screens (subscriber, ONU, box-chain, …): a Card with a
// prominent title, an optional status badge + action slot on the right,
// a loading/busy state, and a stack of label-above-value rows.

import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "../lib/cn";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "./card";
import { Spinner } from "./spinner";
import { Skeleton } from "./skeleton";

export function DataCard({
  title,
  subtitle,
  badge,
  action,
  loading,
  busy,
  footer,
  children,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Status pill rendered top-right (online / active / fiber-down …). */
  badge?: React.ReactNode;
  /** Extra controls top-right, after the badge. */
  action?: React.ReactNode;
  /** Replaces the body with skeletons (first load — nothing to show yet). */
  loading?: boolean;
  /** Header spinner only, body untouched (a background refresh). */
  busy?: boolean;
  /** Pinned to the card's bottom edge — when present the card becomes a
   *  flex column so the footer stays put even if a sibling card in the
   *  same grid row is taller (no ragged gap below short cards). Typical
   *  use: a row of action buttons. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className={footer ? "flex h-full flex-col" : undefined}>
      <CardHeader className="flex-row items-start justify-between gap-2 space-y-0 py-3">
        <div className="min-w-0">
          <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {badge}
          {action}
          {(loading || busy) && <Spinner />}
        </div>
      </CardHeader>
      <CardContent className={cn("pt-0", footer && "flex-1")}>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-3/5" />
          </div>
        ) : (
          children
        )}
      </CardContent>
      {footer && <CardFooter className="flex-wrap gap-2 pt-0">{footer}</CardFooter>}
    </Card>
  );
}

/** A stack of label-above-value rows (Tabler-`datagrid` style). */
export function DataGrid({ children, className }: { children: React.ReactNode; className?: string }) {
  return <dl className={cn("space-y-3", className)}>{children}</dl>;
}

export function DataItem({
  label,
  children,
  mono,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  /** Render the value in the mono font (IPs, MACs, optical readings…). */
  mono?: boolean;
  /** Extra classes on the value (e.g. an RX-power tone colour). */
  className?: string;
}) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={cn("mt-0.5 flex flex-wrap items-center gap-1.5 text-sm", mono && "font-mono", className)}>
        {children}
      </dd>
    </div>
  );
}

/** Copy `text` to the clipboard. Uses the async Clipboard API in a secure
 *  context (HTTPS / localhost); falls back to the legacy hidden-textarea +
 *  execCommand trick otherwise — the dev server is plain http:// from the
 *  box's LAN IP, where navigator.clipboard is undefined. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Inline copy-to-clipboard button — Copy icon, flips to a check for ~1s. */
export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      aria-label={label ? `Copy ${label}` : "Copy"}
      title={label ? `Copy ${label}` : "Copy"}
      onClick={() => {
        void copyToClipboard(value).then((ok) => {
          if (!ok) return;
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        });
      }}
      className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      {done ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
    </button>
  );
}
